import { useTranslation } from "react-i18next";
import { usePayableInvoicesRepository } from "../../../api/repositories/PayableInvoicesRepository";
import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { formatCurrency, formatDate, formatDateWithoutTime } from "../../../utils/FormatUtils";
import { showStatusColumn } from "./PayableInvoiceTableUtil";
import EmptyState from "../../../../../util/component/EmptyState";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { NumericSummary } from "../../../component/NumericSummary";
import { ID_INVOICE } from "../../invoices/constants/InvoicesTypes";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../constants/PayableInvoicesRecoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { FilterDef } from "../../../constants/FinanceModels";
import FinanceDialog from "../../../component/FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { Tooltip, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { DeleteForeverRounded, DescriptionRounded, DownloadRounded, IntegrationInstructionsRounded } from "@mui/icons-material";
import { ID_TO_SEND } from "../constants/PayableInvoicesStatuses";
import DebugXMLInvoice from "../../../experiments/DebugXMLInvoice";
import { useNavigate } from "react-router-dom";
import { useSapRepository } from "../../../api/repositories/SapRepository";
import { useAgGridLocale, useHasScope } from "../../../../../config/globalConfig";
import { DELETE_PAYABLE, DOWNLOAD_PAYABLE_PDF, DOWNLOAD_PAYABLE_XML, VIEW_PAYABLE_DETAIL } from "../../../utils/FinanceScope";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

const PayableInvoicesTable = () => {
    const UNIQUE_ID = "fnn-passive-invoices";

    const theme = useTheme();
    const { t } = useTranslation("finance", { keyPrefix: "payable-invoices" });
    const { getPayableInvoices, getSummary, deleteInvoice, downloadPDF } = usePayableInvoicesRepository();
    const { getPreview } = useSapRepository();
    const hasScope = useHasScope;


    const gridRef = useRef<AgGridReact>(null);
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();

    const [warningOpen, setWarningOpen] = useState(false);
    const [warningTitle, setWarningTitle] = useState("");
    const { globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");
    
    const [xml, setXml] = useState<string>("");
    const [openXml, setOpenXml] = useState<boolean>(false);
    let notViewActions = !hasScope(VIEW_PAYABLE_DETAIL) && !hasScope(DOWNLOAD_PAYABLE_PDF) && !hasScope(DELETE_PAYABLE) && !hasScope(DOWNLOAD_PAYABLE_XML);

    const columnDefs : ColDef[] = [
        {
            field: "invoiceNumber", headerName: t("tables.payable-invoice.id-proforma"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "sapInvoiceNumber", headerName: t("tables.payable-invoice.id-invoice"),
            minWidth: 200,
            valueFormatter: (params) => params.value || '---',
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "vendor.sapId", headerName: t("tables.payable-invoice.id-customer"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "vendor.companyName", headerName: t("tables.payable-invoice.customer-company-name"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "createdDate", headerName: t("tables.payable-invoice.created-date"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDate(params.value),
        },
        {
            field: "invoiceDate", headerName: t("tables.payable-invoice.invoice-date"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "dueDate", headerName: t("tables.payable-invoice.due-date"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "invoiceSchema.description", headerName: t("tables.payable-invoice.payment-terms"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "totalNetPrice", headerName: t("tables.payable-invoice.net-amount"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },
        {
            field: "totalVat", headerName: t("tables.payable-invoice.vat-amount"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },
        {
            field: "totalGrossPrice", headerName: t("tables.payable-invoice.total-amount"),
            minWidth: 190,
            unSortIcon: true, sortable: true,
            valueFormatter: (params) => formatCurrency(params.value, params.data.currency, 0.01),
        },
        {
            field: "paymentMethod.description", headerName: t("tables.payable-invoice.payment-method"),
            minWidth: 220,
            unSortIcon: true, sortable: true
        },
        {
            field: "status", headerName: t("tables.payable-invoice.status"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                return showStatusColumn(status);
            },
        },
        {
            field: "legalEntity", headerName: t("tables.payable-invoice.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.payable-invoice.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities.length <= 1
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                if (notViewActions) return {}

                return {
                    component: () => (

                            <FinanceDynamicActions>
                                {hasScope(VIEW_PAYABLE_DETAIL) &&
                                <Tooltip arrow title={t('tables.tooltips.show-detail')}>
                                    <IconButton
                                            sx={{ color: theme.palette.accent.main }}
                                            onClick={() => window.open(`/finance/invoices-payable/${params.data.id}`, "_blank")}
                                    >
                                        <DescriptionRounded/>
                                    </IconButton>
                                </Tooltip>
                                }
                                {hasScope(DOWNLOAD_PAYABLE_PDF) &&
                                <Tooltip arrow title={t('tables.tooltips.download-pdf')}>
                                    <IconButton
                                            sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                downloadPDF(params.data.id).then((res: any) => {
                                                    if (res?.result) {
                                                        let pdfWindow = window.open("");
                                                        pdfWindow!.document.write(
                                                                `<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(
                                                                        res.result
                                                                )}'></iframe>`
                                                        );
                                                    }
                                                });
                                            }}
                                    >
                                        <DownloadRounded/>
                                    </IconButton>
                                </Tooltip>
                                }
                                {hasScope(DELETE_PAYABLE) &&
                                <Tooltip arrow title={t("tables.tooltips.delete")}>
                                    <IconButton
                                            disabled={params.data.status !== ID_TO_SEND}
                                            sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                setWarningTitle(params.data.id);
                                                setWarningOpen(true);
                                            }}
                                    >
                                        <DeleteForeverRounded/>
                                    </IconButton>
                                </Tooltip>
                                }
                                {hasScope(DOWNLOAD_PAYABLE_XML) &&
                                <Tooltip arrow title={t('tables.tooltips.xml')}>
                                    <IconButton
                                            sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                getPreview(params.data.id).then((it) => {
                                                    setXml(it);
                                                    setOpenXml(true);
                                                });
                                            }}
                                    >
                                        <IntegrationInstructionsRounded/>
                                    </IconButton>
                                </Tooltip>
                                }
                            </FinanceDynamicActions>
                    )
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.payable-invoice.no-rows-overlay.title') }
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].colId;
                let order =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].sort;

                getPayableInvoices(startRow, endRow - startRow, sort, order, filterDefs)
                        .then((response: any) => {
                            let result = response.result;

                            if (result.page.totalElements === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.page.totalElements,
                            });
                        })
                        .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
                <NumericSummary query={() => getSummary(filterDefs)} fields={[ID_INVOICE]} i18nPath={"invoices.counters"}/>
        ),
        renderActions: () => (
                <></>
        )
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api?.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType="serverSide"
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        rowSelection={"multiple"}
                        suppressRowClickSelection
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true
                                })
                            }
                        }}
                        onColumnMoved={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={e => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            setLocalGridState(UNIQUE_ID, {
                                rows: gridRef.current!.api?.paginationGetPageSize(),
                                cols: getLocalGridState(UNIQUE_ID).cols
                            });
                        }}
                ></AgGridCustomTable>
                <FinanceDialog open={warningOpen}
                               onClose={() => setWarningOpen(false)}
                               callback={() => deleteInvoice(warningTitle).then(() => setUpdating(true))}
                               titleIcon={<InfoRoundedIcon sx={{ color: 'accent.main' }}/>}
                               title={t('delete-confirmation.title')}
                               content={t('delete-confirmation.text', { invoiceId: warningTitle })}
                               confirmButton={t('delete-confirmation.confirm')}
                               cancelButton={t('delete-confirmation.cancel')}
                />
                <DebugXMLInvoice xml={xml} open={openXml} onClose={() => {
                    setXml("");
                    setOpenXml(false)
                }}/>
            </>
    );
}
export default PayableInvoicesTable;